import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import WhatsAppIcon from '../components/WhatsAppIcon';

const EMAIL = 'farwingsolutions@gmail.com';
const TYPES = ['Website', 'Mobile app', 'SaaS product', 'AI automation', 'Custom software', 'Not sure yet'];

function Field({ id, label, children }) {
  return (
    <div className="group relative">
      <label htmlFor={id} className="block text-sm text-muted group-focus-within:text-accent transition-colors">
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  'mt-2 block w-full border-0 border-b border-line bg-transparent px-0 py-3 text-lg text-ink placeholder:text-muted/50 focus:border-accent focus:outline-none focus:ring-0 transition-colors';

export default function Contact({ asPage = false }) {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [types, setTypes] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const toggle = (t) => setTypes((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(`https://formsubmit.co/ajax/${EMAIL}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          project_type: types.join(', ') || 'Not specified',
          message: form.message,
          _subject: `Farwings: new enquiry from ${form.name}`,
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus('sent');
      setForm({ name: '', email: '', message: '' });
      setTypes([]);
    } catch (err) {
      console.error('Contact form error', err);
      setStatus('error');
    }
  };

  const Heading = asPage ? 'h1' : 'h2';

  return (
    <section
      id="contact"
      aria-labelledby="contact-title"
      className={`theme-dark sky-dusk relative overflow-hidden ${asPage ? 'pt-36 pb-24 sm:pt-44' : 'py-24 sm:py-32'}`}
    >
      {/* scenery for the glass form */}
      <p
        aria-hidden="true"
        className="display pointer-events-none absolute -right-[3vw] top-[12%] hidden select-none text-right text-[19vw] leading-[0.8] text-white/20 lg:block"
      >
        Let&rsquo;s
        <br />
        <span className="text-[#4FB0E7]/45">build.</span>
      </p>
      <div className="frame relative grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Heading id="contact-title" className="display text-[2.6rem] sm:text-6xl lg:text-[5rem]">
            Tell us about your project.
          </Heading>
          <p className="mt-6 lede">
Tell us the problem. We reply within 24 hours with a plan, timeline and price.
          </p>

          <dl className="mt-12 divide-y divide-line border-y border-line">
            <div className="flex flex-wrap items-baseline justify-between gap-2 py-5">
              <dt className="text-sm text-muted">Email</dt>
              <dd>
                <a href={`mailto:${EMAIL}`} className="link-line break-all">
                  {EMAIL}
                </a>
              </dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2 py-5">
              <dt className="text-sm text-muted">Phone</dt>
              <dd className="flex flex-wrap gap-x-4 gap-y-1">
                <a href="tel:+917043031553" className="link-line">
                  +91 70430 31553
                </a>
                <a href="tel:+918866823010" className="link-line">
                  +91 88668 23010
                </a>
              </dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2 py-5">
              <dt className="text-sm text-muted">WhatsApp</dt>
              <dd>
                <a
                  href="https://wa.me/917043031553"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-semibold text-ink link-line"
                >
                  <WhatsAppIcon size={18} className="text-[#25D366]" />
                  Chat with us
                </a>
              </dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2 py-5">
              <dt className="text-sm text-muted">Based in</dt>
              <dd>Gujarat, India, working worldwide</dd>
            </div>
          </dl>
          <p className="mt-6 flex items-center gap-2 text-sm text-muted">
            <span className="h-2 w-2 rounded-full bg-[#3DDC97] animate-blink" aria-hidden="true" />
            Taking on new projects now
          </p>
        </div>

        <form
          onSubmit={submit}
          data-glass="form"
          className="glass-pro relative z-[25] rounded-[2.25rem] p-6 sm:p-10 lg:col-span-7"
        >
          <div className="on-glass space-y-10">
          <fieldset>
            <legend className="text-sm text-muted">What do you need?</legend>
            <div className="mt-4 flex flex-wrap gap-2">
              {TYPES.map((t) => {
                const on = types.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggle(t)}
                    className={`inline-flex h-11 items-center gap-2 rounded-full border px-4 text-[0.9375rem] transition-colors duration-300 ${
                      on ? 'border-accent bg-accent text-accent-ink' : 'border-white/25 text-ink hover:border-ink'
                    }`}
                  >
                    {on && <Check size={15} aria-hidden="true" />}
                    {t}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="grid gap-10 sm:grid-cols-2">
            <Field id="c-name" label="Your name">
              <input
                id="c-name"
                autoComplete="name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
                placeholder="Aisha Khan"
              />
            </Field>
            <Field id="c-email" label="Email address">
              <input
                id="c-email"
                type="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
                placeholder="you@company.com"
              />
            </Field>
          </div>
          <Field id="c-msg" label="About the project">
            <textarea
              id="c-msg"
              required
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={`${inputCls} resize-none`}
              placeholder="What are you building, and when do you need it?"
            />
          </Field>

          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <button type="submit" disabled={status === 'sending'} className="btn-primary disabled:opacity-60">
              {status === 'sending' ? 'Sending…' : 'Send message'}
            </button>
            <AnimatePresence mode="wait">
              {status === 'sent' && (
                <motion.p
                  key="sent"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  role="status"
                  className="flex items-center gap-2 text-sm text-ink"
                >
                  <Check size={16} className="text-accent" aria-hidden="true" />
                  Message sent. We will reply within 24 hours.
                </motion.p>
              )}
              {status === 'error' && (
                <motion.p key="err" initial={{ opacity: 0 }} animate={{ opacity: 1 }} role="alert" className="text-sm text-[#FF9C9C]">
                  Message not sent. Check your connection and try again, or email us directly.
                </motion.p>
              )}
            </AnimatePresence>
          </div>
          </div>
        </form>
      </div>
    </section>
  );
}
